import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logo from '../../../../img/logo rio verde pdf.png';
import './DesramaTablePopup.css';
import { API_BASE_URL } from "../../../../config";
import { FaFilePdf, FaTrashAlt, FaExclamationTriangle } from 'react-icons/fa';
import { generateDesramaReport } from './reportGeneratorDesrama';

const DesramaTablePopup = ({ isOpen, onClose, imovelId }) => {
    const [desramaData, setDesramaData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [itemToDeleteId, setItemToDeleteId] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const navigate = useNavigate();

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    };

    // FUNÇÃO ALTERADA E MAIS ROBUSTA
    const formatNumber = (value) => {
        // Se o valor for nulo ou indefinido, retorna "N/A"
        if (value === null || value === undefined) {
            return "N/A";
        }
    
        // Converte o valor para string para poder usar o método .replace()
        const stringValue = String(value);
    
        // Substitui a vírgula por ponto para o caso do dado já vir como "10,5"
        // e o parseFloat o interpretar incorretamente.
        const number = parseFloat(stringValue.replace(',', '.'));
    
        // Se após a conversão, o resultado não for um número válido, retorna "N/A"
        if (isNaN(number)) {
            return "N/A";
        }
    
        // Agora, com a certeza de que é um número, formata para o padrão brasileiro.
        return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const fetchDesramas = useCallback(async () => {
        if (!imovelId) return;

        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/imoveis/${imovelId}/desramas`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = Array.isArray(response.data) ? response.data : [];
            const registrosCompletos = data.filter(desrama =>
                desrama.altura !== null &&
                desrama.data !== null &&
                desrama.numero !== null
            );
            setDesramaData(registrosCompletos);
        } catch (error) {
            console.error("Erro ao buscar desramas:", error);
            if (error.response) {
                if (error.response.status === 401 || error.response.status === 400) {
                    localStorage.removeItem('token');
                    navigate('/');
                } else if (error.response.status === 404) {
                    setDesramaData([]);
                } else {
                    setError("Não foi possível carregar os dados de desrama.");
                }
            } else {
                setError("Erro de conexão ao buscar os dados.");
            }
        } finally {
            setLoading(false);
        }
    }, [imovelId, navigate]);

    useEffect(() => {
        if (isOpen) {
            fetchDesramas();
        } else {
            setDesramaData([]);
            setLoading(true);
            setError(null);
            setShowDeleteConfirmation(false);
            setItemToDeleteId(null);
            setSelectedItems([]);
        }
    }, [isOpen, fetchDesramas]);

    const handleSelectItem = (id) => {
        setSelectedItems(prev =>
            prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = desramaData.map(item => item.id);
            setSelectedItems(allIds);
        } else {
            setSelectedItems([]);
        }
    };

    const confirmDelete = (id) => {
        setItemToDeleteId(id);
        setShowDeleteConfirmation(true);
    };

    const cancelDelete = () => {
        setItemToDeleteId(null);
        setShowDeleteConfirmation(false);
    };

    const executeDelete = async () => {
        if (!itemToDeleteId) return;

        const token = localStorage.getItem('token');
        if (!token) {
            setError("Sessão expirada. Faça login novamente.");
            navigate('/');
            return;
        }

        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/imoveis/${imovelId}/desramas/${itemToDeleteId}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.status === 200) {
                setDesramaData(prevData => prevData.filter(desrama => desrama.id !== itemToDeleteId));
                setSelectedItems(prev => prev.filter(id => id !== itemToDeleteId));
            }
        } catch (err) {
            if (err.response && (err.response.status === 401 || err.response.status === 400)) {
                localStorage.removeItem('token');
                navigate('/');
            } else {
                console.error("Erro ao excluir desrama:", err);
                setError(err.response?.data?.error || "Erro ao excluir o registro de desrama.");
            }
        } finally {
            cancelDelete();
        }
    };

    const handleGenerateReport = async () => {
        const selectedData = desramaData.filter(item => selectedItems.includes(item.id));
        
        // Adiciona um 'await' para esperar a conclusão
        await generateDesramaReport({
            selectedData,
            formatDate,
            formatNumber,
            imovelId,
            logo
        });
    };

    if (!isOpen) return null;

    return (
        <>
            {showDeleteConfirmation && (
                <div className="delete-confirmation-overlay" onClick={cancelDelete}>
                    <div className="delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
                        <FaExclamationTriangle className="warning-icon" />
                        <h3>Confirmar Exclusão</h3>
                        <p>Tem certeza que deseja excluir este registro de desrama? A ação não pode ser desfeita.</p>
                        <div className="modal-footer-stacked">
                            <button className="btn-danger-stacked" onClick={executeDelete}>
                                <FaTrashAlt /> Confirmar Exclusão
                            </button>
                            <button className="btn-link-stacked" onClick={cancelDelete}>
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="desrama-modal-overlay" onClick={onClose}>
                <div className="desrama-modal-content" onClick={(e) => e.stopPropagation()}>
                    <header className="desrama-modal-header">
                        <h2 className="desrama-modal-title">Relatório de Desramas</h2>
                        <button className="desrama-modal-close-btn" onClick={onClose}>&times;</button>
                    </header>

                    <main className="desrama-modal-body">
                        {loading ? ( <p className="desrama-loading-message">Carregando...</p> ) :
                        error ? ( <p className="desrama-error-message">{error}</p> ) :
                        desramaData.length > 0 ? (
                            <div className="desrama-table-responsive">
                                <table className="desrama-table">
                                    <thead>
                                        <tr>
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    onChange={handleSelectAll}
                                                    checked={selectedItems.length === desramaData.length && desramaData.length > 0}
                                                    ref={el => el && (el.indeterminate = selectedItems.length > 0 && selectedItems.length < desramaData.length)}
                                                    title="Selecionar Todos"
                                                />
                                            </th>
                                            <th>Número</th>
                                            <th>Altura</th>
                                            <th>Data</th>
                                            <th>Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {desramaData.map((desrama) => (
                                            <tr key={desrama.id} className={selectedItems.includes(desrama.id) ? 'selected-row' : ''}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedItems.includes(desrama.id)}
                                                        onChange={() => handleSelectItem(desrama.id)}
                                                    />
                                                </td>
                                                <td>{desrama.numero}</td>
                                                <td>{formatNumber(desrama.altura)}</td>
                                                <td>{formatDate(desrama.data)}</td>
                                                <td>
                                                    <button className="action-btn-desrama btn-delete-desrama" onClick={() => confirmDelete(desrama.id)}>
                                                        <FaTrashAlt />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : ( <p className="desrama-no-data">Nenhuma desrama registrada para este imóvel.</p> )}
                    </main>

                    <footer className="desrama-modal-footer">
                        <button type="button" className="modal-btn-desrama btn-cancel-desrama" onClick={onClose}>
                            Fechar
                        </button>
                        <button
                            type="button"
                            className="modal-btn-desrama btn-success-desrama"
                            onClick={handleGenerateReport}
                            disabled={selectedItems.length === 0}
                        >
                           <FaFilePdf /> Gerar Relatório
                        </button>
                    </footer>
                </div>
            </div>
        </>
    );
};

export default DesramaTablePopup;